var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import React, { useEffect, useState, useRef } from "react";
import { ChooseSubscriptionScreen, activeFulfillmentIdVar, saveToFutureFulfillmentsVar, Button, ButtonVariant, primaryActionLoadingVar, startPrimaryAction, completePrimaryAction, TextInput, useUpdateUser, TextAreaInput, } from '@withfriendsco/wf-components';
import { CountryDropdown, RegionDropdown } from "react-country-region-selector";
import WelcomeGiftVariant from "./WelcomeGiftVariant";
import { useApolloClient, gql, useQuery } from "@apollo/client";
import { DeliveryMethod } from "types";
import _ from "underscore";
const VARIANT_SCREEN_ADDRESS_FRAGMENT = gql `
  fragment VariantScreenAddress on Address {
    shippingName
    line1
    line2
    city
    state
    zip
    country
  }
`;
const VariantScreen = ({ subscriptionBenefitFulfillmentIds, welcomeGiftFulfillmentId, isAfterPurchase }) => {
    var _a, _b, _c, _d;
    const client = useApolloClient();
    const fulfillmentRefs = useRef({});
    const [deliveryMethods, setDeliveryMethods] = useState({});
    const [address, setAddress] = useState({
        shippingName: "",
        line1: "",
        line2: "",
        city: "",
        state: "",
        zip: "",
        country: "United States",
        phoneNumber: "",
    });
    const [highlightedFields, setHighlightedFields] = useState([]);
    const [saveMessage, setSaveMessage] = useState(null);
    const [shippingNotes, setShippingNotes] = useState("");
    const allFulfillmentIds = [welcomeGiftFulfillmentId, ...subscriptionBenefitFulfillmentIds].filter(Boolean);
    const { data } = useQuery(gql `
    query VariantScreenMember($id: ID!) {
      fulfillment(id: $id) {
        id
        address {
          ...VariantScreenAddress
        }
        shippingNotes
        benefit {
          id
          ... on SubscriptionBenefit {
            shippingNotesInstructions
          }
          ... on WelcomeGiftBenefit {
            shippingNotesInstructions
          }
          ... on DriveBenefit {
            shippingNotesInstructions
          }
        }
        membership {
          id
          user {
            id
            phoneNumber
          }
          unfulfilledFulfillments: fulfillments(query: { isFulfilled: false}) {
            id
            address {
              ...VariantScreenAddress
            }
            shippingNotes
            benefit {
              id
              ... on SubscriptionBenefit {
                shippingNotesInstructions
              }
              ... on WelcomeGiftBenefit {
                shippingNotesInstructions
              }
              ... on DriveBenefit {
                shippingNotesInstructions
              }
            }
          }
        }
      }
    }
    ${VARIANT_SCREEN_ADDRESS_FRAGMENT}
  `, {
        variables: { id: allFulfillmentIds[0] },
        skip: allFulfillmentIds.length === 0,
    });
    useEffect(() => {
        var _a, _b;
        if (!(data === null || data === void 0 ? void 0 : data.fulfillment)) {
            return;
        }
        const { fulfillment } = data;
        const { membership } = fulfillment;
        const allFulfillments = [
            data.fulfillment,
            ...(data.fulfillment.membership.unfulfilledFulfillments || [])
        ];
        const addressToPrefill = allFulfillments
            .map(f => f.address)
            .find(a => a && a.line1 && a.city && a.state && a.zip && a.country);
        const phoneToPrefill = (_a = data.fulfillment.membership.user) === null || _a === void 0 ? void 0 : _a.phoneNumber;
        setAddress(prev => (Object.assign(Object.assign(Object.assign({}, prev), (addressToPrefill && _.pick(addressToPrefill, 'shippingName', 'line1', 'line2', 'city', 'state', 'zip', 'country'))), (phoneToPrefill && { phoneNumber: phoneToPrefill }))));
        const noteToPrefill = (_b = allFulfillments.find(f => f.shippingNotes)) === null || _b === void 0 ? void 0 : _b.shippingNotes;
        if (noteToPrefill) {
            setShippingNotes(noteToPrefill);
        }
    }, [data]);
    const memberId = (_c = (_b = (_a = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _a === void 0 ? void 0 : _a.membership) === null || _b === void 0 ? void 0 : _b.user) === null || _c === void 0 ? void 0 : _c.id;
    const { wrapped: updateUser } = useUpdateUser({ userId: memberId });
    const handleDeliveryMethodChange = (fulfillmentId, method) => {
        setDeliveryMethods(prev => (Object.assign(Object.assign({}, prev), { [fulfillmentId]: method })));
    };
    const showAddressForm = Object.values(deliveryMethods).some(m => m === DeliveryMethod.Shipping);
    const updateAddress = (field, value) => {
        setAddress(prev => (Object.assign(Object.assign({}, prev), { [field]: value })));
    };
    const checkRequiredFields = () => {
        if (!showAddressForm)
            return [];
        const required = ["line1", "city", "state", "zip", "country"];
        return required.filter(field => !address[field] || address[field] === "");
    };
    const handleSave = async () => {
        let allBlankFields = [];
        // Validate children
        allFulfillmentIds.forEach(id => {
            const ref = fulfillmentRefs.current[id];
            if (ref && ref.checkRequiredFields) {
                const childErrors = ref.checkRequiredFields();
                if (childErrors.length > 0) {
                    allBlankFields = [...allBlankFields, ...childErrors];
                }
            }
        });
        // Validate address form in parent
        const blankAddressFields = checkRequiredFields();
        if (blankAddressFields.length > 0) {
            setHighlightedFields(blankAddressFields);
            allBlankFields = [...allBlankFields, ...blankAddressFields];
        }
        if (allBlankFields.length > 0) {
            setSaveMessage(_jsxs("div", { children: ["Error! The following required fields were blank: ", allBlankFields.join(', ')] }));
            return;
        }
        setHighlightedFields([]);
        setSaveMessage(null);
        startPrimaryAction();
        if (memberId) {
            const { phoneNumber } = address, addressFields = __rest(address, ["phoneNumber"]);
            const userInput = {
                address: showAddressForm ? addressFields : undefined,
                phoneNumber,
            };
            await updateUser(userInput);
        }
        const savePromises = allFulfillmentIds.map(id => {
            const ref = fulfillmentRefs.current[id];
            if (ref) {
                return ref.save({
                    sharedAddress: showAddressForm ? address : undefined,
                    shippingNotes: shippingNotes
                });
            }
            return Promise.resolve();
        });
        try {
            await Promise.all(savePromises);
            saveToFutureFulfillmentsVar(false);
            activeFulfillmentIdVar(null);
            setSaveMessage(_jsx("div", { children: "Settings saved successfully!" }));
            for (const el of document.getElementsByClassName("wf-button-back")) {
                if (el instanceof HTMLElement) {
                    el.click();
                }
            }
        }
        catch (e) {
            console.error("[DEBUG] Full error object:", e);
            setSaveMessage(_jsxs("div", { children: ["An error occurred: ", e.message] }));
        }
        finally {
            completePrimaryAction();
        }
    };
    const allShippingNotesInstructions = (data === null || data === void 0 ? void 0 : data.fulfillment) ? [
        data.fulfillment.benefit.shippingNotesInstructions,
        ...(((_d = data.fulfillment.membership.unfulfilledFulfillments) === null || _d === void 0 ? void 0 : _d.map(f => f.benefit.shippingNotesInstructions)) || [])
    ].filter(Boolean) : [];
    const uniqueShippingNotesInstructions = [...new Set(allShippingNotesInstructions)];
    return (_jsxs("div", { className: "flex flex-wrap justify-center font-sans px-4", children: [allFulfillmentIds.map((id) => {
                const isWelcomeGift = id === welcomeGiftFulfillmentId;
                const refCallback = (el) => {
                    if (el) {
                        fulfillmentRefs.current[id] = el;
                    }
                    else {
                        delete fulfillmentRefs.current[id];
                    }
                };
                if (isWelcomeGift) {
                    return (_jsx(WelcomeGiftVariant, { ref: refCallback, id: id, isEmbedded: true, isAfterPurchase: isAfterPurchase, onDeliveryMethodChange: (method) => handleDeliveryMethodChange(id, method), isUserUpdateHandledByParent: true }, id));
                }
                return (_jsx(ChooseSubscriptionScreen, { ref: refCallback, id: id, isEmbedded: true, isAfterPurchase: isAfterPurchase, onDeliveryMethodChange: (method) => handleDeliveryMethodChange(id, method), isUserUpdateHandledByParent: true }, id));
            }), showAddressForm && (_jsxs("div", { className: "w-full max-w-xl", children: [_jsx("h3", { className: "my-4 block", children: _jsx("strong", { children: "Shipping Preferences" }) }), _jsx("div", { className: "text-wfRed-600 text-base my-2", children: "* Required" }), _jsx("label", { className: "my-4 block w-full", children: "Shipping Name" }), _jsx(TextInput, { value: address.shippingName, onChange: (e) => updateAddress("shippingName", e.target.value), className: "p-2 w-full text-lg" }), _jsxs("label", { className: "my-4 block w-full", children: ["Address Line 1 ", _jsx("span", { className: "text-wfRed-600", children: "*" })] }), _jsx(TextInput, { required: true, value: address.line1, onChange: (e) => updateAddress("line1", e.target.value), className: `p-2 w-full text-lg ${_.contains(highlightedFields, "line1") ? "border-salmon-600" : "border-wfGray-300"}`, placeholder: "123 Main St." }), _jsx("label", { className: "my-4 block w-full", children: "Address Line 2" }), _jsx(TextInput, { value: address.line2, onChange: (e) => updateAddress("line2", e.target.value), className: "p-2 w-full text-lg" }), _jsxs("div", { className: "sm:grid sm:grid-cols-4 sm:gap-4", children: [_jsxs("div", { className: "sm:col-span-2", children: [_jsxs("label", { className: "my-4 block w-full", children: ["City ", _jsx("span", { className: "text-wfRed-600", children: "*" })] }), _jsx(TextInput, { required: true, value: address.city, onChange: (e) => updateAddress("city", e.target.value), className: `p-2 w-full text-lg ${_.contains(highlightedFields, "city") ? "border-salmon-600" : "border-wfGray-300"}`, placeholder: "New York" })] }), _jsxs("div", { className: "sm:col-span-2", children: [_jsxs("label", { className: "my-4 block w-full", children: ["ZIP ", _jsx("span", { className: "text-wfRed-600", children: "*" })] }), _jsx(TextInput, { required: true, value: address.zip, onChange: (e) => updateAddress("zip", e.target.value), className: `p-2 w-full text-lg ${_.contains(highlightedFields, "zip") ? "border-salmon-600" : "border-wfGray-300"}`, placeholder: "12345" })] })] }), _jsxs("div", { children: [_jsxs("label", { className: "my-4 block w-full", htmlFor: "state", children: ["State ", _jsx("span", { className: "text-wfRed-600", children: "*" })] }), _jsx(RegionDropdown, { name: "state", classes: `my-4 block w-full p-2 border focus:border-salmon-600 focus:border-2 text-lg ${_.contains(highlightedFields, "state")
                                    ? "border-salmon-600"
                                    : "border-wfGray-300"}`, country: address.country || "United States", value: address.state, onChange: (val) => updateAddress("state", val) })] }), _jsxs("div", { children: [_jsxs("label", { className: "my-4 block w-full", htmlFor: "country", children: ["Country ", _jsx("span", { className: "text-wfRed-600", children: "*" })] }), _jsx(CountryDropdown, { name: "country", classes: `my-4 block w-full p-2 border focus:border-salmon-600 focus:border-2 text-lg ${_.contains(highlightedFields, "country")
                                    ? "border-salmon-600"
                                    : "border-wfGray-300"}`, value: address.country || "United States", onChange: (val) => updateAddress("country", val) })] }), _jsx("label", { className: "my-4 block w-full", children: "Phone Number" }), _jsx(TextInput, { value: address.phoneNumber, onChange: (e) => updateAddress("phoneNumber", e.target.value), className: "p-2 w-full text-lg" }), _jsx("label", { className: "my-4 block w-full", children: "Shipping Notes" }), uniqueShippingNotesInstructions.length > 0 && (_jsx("div", { className: "w-full my-4", children: uniqueShippingNotesInstructions.map((instructions, index) => (_jsx("p", { className: "my-2", dangerouslySetInnerHTML: { __html: instructions.replace(/\\n/g, '<br>') } }, index))) })), _jsx(TextAreaInput, { value: shippingNotes, onChange: (e) => {
                            setShippingNotes(e.target.value);
                        }, className: "p-2 w-full text-lg my-2" })] })), _jsxs("div", { className: "w-full max-w-xl mt-4", children: [saveMessage, _jsx(Button, { className: "my-4", variant: ButtonVariant.Salmon, disabled: primaryActionLoadingVar(), onClick: handleSave, children: "Save settings" })] })] }));
};
export default VariantScreen;
